import React, { useState, useEffect } from "react";
import { fetchCampsites, fetchRecAreas } from "../services/supabaseProxy";
import { getFacilitiesByState, cleanReservationUrl, getAllFacilities, getAllFacilityTypes, getFacilitiesByType, getFacilitiesByStateAndType, searchFacilities } from "../services/moabFacilitiesQuery";

// US States
const US_STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'
];

const CampgroundInfo = () => {
  const [campgrounds, setCampgrounds] = useState([]);
  const [selectedCampground, setSelectedCampground] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [error, setError] = useState(null);
  const [offset, setOffset] = useState(0);
  const [recAreas, setRecAreas] = useState({});
  const [selectedState, setSelectedState] = useState(null);
  const [totalResults, setTotalResults] = useState(0);
  const [selectedType, setSelectedType] = useState(null);
  const [allFacilityTypes, setAllFacilityTypes] = useState([]);

  // Fetch campgrounds from Supabase (default) or by state or by type
  const fetchCampgrounds = async (query = "", pageOffset = 0, state = selectedState, type = selectedType) => {
    setLoading(true);
    setError(null);
    try {
      let campgroundsList = [];
      let total = 0;

      // If search query is provided, perform full text search
      if (query && query.trim().length > 0) {
        console.log('Searching facilities for:', query, 'offset:', pageOffset);
        const result = await searchFacilities(query, pageOffset, 50);
        console.log('searchFacilities result:', result);
        campgroundsList = result.all || [];
        total = result.total || 0;
      } else if (state && type) {
        // If both state and type are selected, fetch facilities by both
        console.log('Fetching facilities for state:', state, 'type:', type, 'offset:', pageOffset);
        const result = await getFacilitiesByStateAndType(state, type, pageOffset, 50);
        console.log('getFacilitiesByStateAndType result:', result);
        campgroundsList = result.all || [];
        total = result.total || 0;
      } else if (type) {
        // If only type is selected, fetch facilities by type
        console.log('Fetching facilities for type:', type, 'offset:', pageOffset);
        const result = await getFacilitiesByType(type, pageOffset, 50);
        console.log('getFacilitiesByType result:', result);
        campgroundsList = result.all || [];
        total = result.total || 0;
      } else if (state) {
        // If only state is selected, fetch facilities by state
        console.log('Fetching facilities for state:', state, 'offset:', pageOffset);
        const result = await getFacilitiesByState(state, pageOffset, 50);
        console.log('getFacilitiesByState result:', result);
        campgroundsList = result.all || [];
        total = result.total || 0;
      } else {
        // Default: fetch from Supabase facilities table
        console.log('Fetching all facilities from Supabase, offset:', pageOffset);
        const result = await getAllFacilities(pageOffset, 50);
        console.log('getAllFacilities result:', result);
        campgroundsList = result.all || [];
        total = result.total || 0;
      }

      console.log('Setting campgrounds:', campgroundsList);
      setCampgrounds(Array.isArray(campgroundsList) ? campgroundsList : []);
      setTotalResults(total);
      setOffset(pageOffset);
    } catch (err) {
      console.error('Error fetching campgrounds:', err);
      setError(err.message);
      setCampgrounds([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch RecArea name by ID
  const fetchRecAreaName = async (recAreaId) => {
    if (!recAreaId || recAreas[recAreaId]) return; // Already cached or no ID
    
    try {
      const data = await fetchRecAreas(recAreaId);
      const recAreaData = data?.RECDATA || data;
      setRecAreas(prev => ({
        ...prev,
        [recAreaId]: recAreaData?.RecAreaName || "Unknown Area"
      }));
    } catch (err) {
      console.error(`Failed to fetch RecArea ${recAreaId}:`, err);
    }
  };

  // Fetch all facility types on mount
  useEffect(() => {
    const loadTypes = async () => {
      try {
        const types = await getAllFacilityTypes();
        setAllFacilityTypes(types);
      } catch (err) {
        console.error('Failed to load facility types:', err);
      }
    };
    loadTypes();
  }, []);

  // Initial load
  useEffect(() => {
    fetchCampgrounds();
  }, []);

  // Reload when state changes
  useEffect(() => {
    setSelectedCampground(null); // Clear selected campground
    setSearchQuery(""); // Clear search query
    setSelectedType(null); // Clear type filter
    setOffset(0); // Reset pagination
    fetchCampgrounds("", 0, selectedState);
  }, [selectedState]);

  // Reset pagination when type filter changes
  useEffect(() => {
    setOffset(0);
    setSelectedCampground(null);
    fetchCampgrounds("", 0, selectedState, selectedType);
  }, [selectedType]);

  // Fetch RecArea when campground is selected
  useEffect(() => {
    if (selectedCampground && selectedCampground.RecAreaID) {
      fetchRecAreaName(selectedCampground.RecAreaID);
    }
  }, [selectedCampground]);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchCampgrounds(searchQuery, 0);
  };

  // Filter campgrounds based on search query (client-side filtering for non-search results)
  const filteredCampgrounds = searchQuery ? campgrounds : campgrounds.filter(camp => {
    const campName = (camp.CampsiteName || camp.FacilityName || "").toLowerCase();
    const query = (searchQuery || "").toLowerCase();
    
    const matchesName = campName.includes(query);
    
    return matchesName;
  });

  // For state-based searches, use server-side pagination. For RIDB searches, use client-side pagination.
  const ITEMS_PER_PAGE = 50;
  // Use filtered campgrounds for display
  const paginatedCampgrounds = filteredCampgrounds;

  const handleNextPage = () => {
    fetchCampgrounds(searchQuery, offset + ITEMS_PER_PAGE, selectedState, selectedType);
  };

  const handlePrevPage = () => {
    if (offset > 0) {
      fetchCampgrounds(searchQuery, Math.max(0, offset - ITEMS_PER_PAGE), selectedState, selectedType);
    }
  };

  return (
    <div style={{ display: "flex", height: "100%", fontFamily: "Arial, sans-serif", fontSize: "12px" }}>
      {/* Left Panel - Campground List */}
      <div style={{
        flex: "0 0 45%",
        borderRight: "1px solid #ccc",
        display: "flex",
        flexDirection: "column",
        background: "#f5f5f5"
      }}>
        {/* State Selector */}
        <div style={{
          padding: "8px",
          borderBottom: "1px solid #ccc",
          background: "#fff"
        }}>
          <label style={{ fontSize: "11px", fontWeight: "bold", display: "block", marginBottom: "4px" }}>
            Browse by State:
          </label>
          <select
            value={selectedState || ""}
            onChange={(e) => setSelectedState(e.target.value || null)}
            style={{
              width: "100%",
              padding: "6px",
              border: "1px solid #999",
              borderRadius: "3px",
              fontSize: "11px",
              boxSizing: "border-box",
              background: "white"
            }}
          >
            <option value="">-- Select a state --</option>
            {US_STATES.map(state => (
              <option key={state} value={state}>
                {state}
              </option>
            ))}
          </select>
        </div>

        {/* Type Filter */}
        <div style={{
          padding: "8px",
          borderBottom: "1px solid #ccc",
          background: "#fff"
        }}>
          <label style={{ fontSize: "11px", fontWeight: "bold", display: "block", marginBottom: "4px" }}>
            Filter by Type:
          </label>
          <select
            value={selectedType || ""}
            onChange={(e) => setSelectedType(e.target.value || null)}
            style={{
              width: "100%",
              padding: "6px",
              border: "1px solid #999",
              borderRadius: "3px",
              fontSize: "11px",
              boxSizing: "border-box",
              background: "white"
            }}
          >
            <option value="">-- All Types --</option>
            {allFacilityTypes.map(type => (
              <option key={type} value={type}>
                {type}
              </option>
            ))}
          </select>
        </div>
        <form onSubmit={handleSearch} style={{ padding: "8px", borderBottom: "1px solid #ccc" }}>
          <input
            type="text"
            placeholder="Search campgrounds..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{
              width: "100%",
              padding: "4px",
              border: "1px solid #999",
              fontSize: "11px",
              boxSizing: "border-box",
            }}
          />
          <button
            type="submit"
            style={{
              width: "100%",
              marginTop: "4px",
              padding: "4px",
              background: "#0066cc",
              color: "white",
              border: "none",
              cursor: "pointer",
              fontSize: "11px",
            }}
          >
            Search
          </button>
        </form>

        {/* Loading / Error */}
        {loading && <div style={{ padding: "8px", color: "#666" }}>Loading...</div>}
        {error && <div style={{ padding: "8px", color: "#c00", fontSize: "11px" }}>Error: {error}</div>}

        {/* Help text if API key not set */}
        {!loading && error && error.includes("API key") && (
          <div style={{ padding: "8px", fontSize: "10px", color: "#666", background: "#fffacd" }}>
            <strong>Setup Required:</strong> Add your RIDB API key to .env as REACT_APP_RECREATION_API_KEY
          </div>
        )}

        {/* Campground List */}
        <div style={{ flex: 1, overflowY: "auto", minHeight: 0 }}>
          {filteredCampgrounds.length === 0 && !loading && (
            <div style={{ padding: "8px", color: "#666" }}>
              {searchQuery ? `No campgrounds match "${searchQuery}"` : "No campgrounds found"}
            </div>
          )}
          {filteredCampgrounds.length > 0 && (
            <div style={{ fontSize: "10px", color: "#999", padding: "4px", background: "#eee" }}>
              Showing {offset + 1}-{Math.min(offset + ITEMS_PER_PAGE, filteredCampgrounds.length)} of {filteredCampgrounds.length} campgrounds
            </div>
          )}
          {paginatedCampgrounds.map((camp, index) => {
            const campId = camp.CampsiteID || camp.FacilityID || camp.id || index;
            const campName = camp.CampsiteName || camp.FacilityName || camp.name || "Unnamed";
            const isSelected = selectedCampground?.CampsiteID === camp.CampsiteID || selectedCampground?.FacilityID === camp.FacilityID || selectedCampground?.id === camp.id;
            
            return (
              <div
                key={campId}
                onClick={() => setSelectedCampground(camp)}
                style={{
                  padding: "8px",
                  borderBottom: "1px solid #ddd",
                  cursor: "pointer",
                  background: isSelected ? "#e8f4f8" : "#fff",
                  fontWeight: isSelected ? "bold" : "normal",
                }}
              >
                <div style={{ fontWeight: "bold", fontSize: "11px" }}>{campName}</div>
                <div style={{ fontSize: "10px", color: "#666" }}>{campId}</div>
              </div>
            );
          })}
        </div>

        {/* Pagination - Works for all searches */}
        {(selectedState || campgrounds.length >= ITEMS_PER_PAGE || offset > 0) && (
          <div style={{
            display: "flex",
            gap: "4px",
            padding: "8px",
            borderTop: "1px solid #ccc",
            background: "#f5f5f5"
          }}>
            <button
              onClick={handlePrevPage}
              disabled={offset === 0}
              style={{
                flex: 1,
                padding: "4px",
                background: offset === 0 ? "#ccc" : "#0066cc",
                color: "#fff",
                border: "none",
                cursor: offset === 0 ? "default" : "pointer",
                fontSize: "10px",
                opacity: offset === 0 ? 0.5 : 1,
              }}
            >
              ← Previous
            </button>
            <div style={{
              flex: 1,
              textAlign: "center",
              fontSize: "10px",
              padding: "4px",
            }}>
              {offset + 1}-{offset + filteredCampgrounds.length} of {totalResults}
            </div>
            <button
              onClick={handleNextPage}
              disabled={(selectedState || selectedType) ? (offset + ITEMS_PER_PAGE >= totalResults) : (campgrounds.length < ITEMS_PER_PAGE)}
              style={{
                flex: 1,
                padding: "4px",
                background: ((selectedState || selectedType) ? (offset + ITEMS_PER_PAGE >= totalResults) : (campgrounds.length < ITEMS_PER_PAGE)) ? "#ccc" : "#0066cc",
                color: "#fff",
                border: "none",
                cursor: ((selectedState || selectedType) ? (offset + ITEMS_PER_PAGE >= totalResults) : (campgrounds.length < ITEMS_PER_PAGE)) ? "default" : "pointer",
                fontSize: "10px",
                opacity: ((selectedState || selectedType) ? (offset + ITEMS_PER_PAGE >= totalResults) : (campgrounds.length < ITEMS_PER_PAGE)) ? 0.5 : 1,
              }}
            >
              Next →
            </button>
          </div>
        )}
      </div>

      {/* Right Panel - Campground Details */}
      <div style={{
        flex: "0 0 55%",
        padding: "12px",
        overflowY: "auto",
        background: "#fff"
      }}>
        {selectedCampground ? (
          <div>
            <h3 style={{ margin: "0 0 12px 0", fontSize: "13px" }}>
              {selectedCampground.CampsiteName || selectedCampground.FacilityName || "Facility"}
            </h3>

            {/* Reservation URL Section */}
            {selectedCampground.FacilityReservationURL && (
              <div style={{
                marginBottom: "12px",
                padding: "8px",
                background: "#e8f4f8",
                border: "1px solid #0066cc",
                borderRadius: "3px",
              }}>
                <div style={{ fontSize: "10px", fontWeight: "bold", marginBottom: "4px", color: "#0066cc" }}>
                  RESERVATIONS AVAILABLE
                </div>
                <div style={{ fontSize: "10px", marginBottom: "6px", color: "#666" }}>
                  Booking Platform: <strong>{(() => {
                    try {
                      const cleanedUrl = cleanReservationUrl(selectedCampground.FacilityReservationURL);
                      return new URL(cleanedUrl).hostname;
                    } catch {
                      return 'Reservation System';
                    }
                  })()}</strong>
                </div>
                <a
                  href={cleanReservationUrl(selectedCampground.FacilityReservationURL)}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: "inline-block",
                    padding: "6px 12px",
                    background: "#0066cc",
                    color: "white",
                    textDecoration: "none",
                    borderRadius: "3px",
                    fontSize: "11px",
                    fontWeight: "bold",
                    cursor: "pointer"
                  }}
                >
                  ➜ View Availability & Book
                </a>
              </div>
            )}

            <div style={{ marginBottom: "12px", fontSize: "12px", lineHeight: "1.6" }}>
              <div style={{ marginBottom: "8px" }}>
                <strong>ID:</strong><br />
                {selectedCampground.CampsiteID || selectedCampground.FacilityID}
              </div>

              {selectedCampground.RecAreaID && (
                <div style={{ marginBottom: "8px" }}>
                  <strong>Recreation Area:</strong><br />
                  {recAreas[selectedCampground.RecAreaID] || "Loading..."}
                </div>
              )}

              {(selectedCampground.CampsiteLatitude || selectedCampground.FacilityLatitude) && (selectedCampground.CampsiteLongitude || selectedCampground.FacilityLongitude) && (
                <div style={{ marginBottom: "8px" }}>
                  <strong>Coordinates:</strong><br />
                  <a 
                    href={`https://www.google.com/maps/search/${selectedCampground.CampsiteLatitude || selectedCampground.FacilityLatitude},${selectedCampground.CampsiteLongitude || selectedCampground.FacilityLongitude}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: "#0066cc", cursor: "pointer", textDecoration: "underline" }}
                  >
                    Lat: {selectedCampground.CampsiteLatitude || selectedCampground.FacilityLatitude}<br />
                    Lng: {selectedCampground.CampsiteLongitude || selectedCampground.FacilityLongitude}
                  </a>
                </div>
              )}

              {selectedCampground.FacilityTypeDescription && (
                <div style={{ marginBottom: "8px" }}>
                  <strong>Type:</strong><br />
                  {selectedCampground.FacilityTypeDescription}
                </div>
              )}

              {selectedCampground.Reservable !== undefined && (
                <div style={{ marginBottom: "8px" }}>
                  <strong>Reservable:</strong><br />
                  {selectedCampground.Reservable ? "Yes" : "No"}
                </div>
              )}

              {selectedCampground.FacilityPhone && (
                <div style={{ marginBottom: "8px" }}>
                  <strong>Phone:</strong><br />
                  <a 
                    href={`tel:${selectedCampground.FacilityPhone}`}
                    style={{ color: "#0066cc", textDecoration: "none", cursor: "pointer" }}
                  >
                    {selectedCampground.FacilityPhone}
                  </a>
                </div>
              )}

              {selectedCampground.FacilityUseFeeDescription && (
                <div style={{ marginBottom: "8px" }}>
                  <strong>Fees:</strong><br />
                  <div 
                    style={{ 
                      fontSize: "11px", 
                      color: "#333", 
                      lineHeight: "1.4",
                      padding: "6px",
                      background: "#f9f9f9",
                      border: "1px solid #eee",
                      borderRadius: "3px"
                    }}
                    dangerouslySetInnerHTML={{ __html: selectedCampground.FacilityUseFeeDescription }}
                  />
                </div>
              )}

              {selectedCampground.FacilityDirections && (
                <div style={{ marginBottom: "8px" }}>
                  <strong>Directions:</strong><br />
                  <div 
                    style={{ 
                      fontSize: "11px", 
                      color: "#333", 
                      lineHeight: "1.4",
                      padding: "6px",
                      background: "#f9f9f9",
                      border: "1px solid #eee",
                      borderRadius: "3px"
                    }}
                    dangerouslySetInnerHTML={{ __html: selectedCampground.FacilityDirections }}
                  />
                </div>
              )}

              {selectedCampground.FacilityDescription && (
                <div style={{ marginBottom: "12px" }}>
                  <strong>Description:</strong><br />
                  <div 
                    style={{ 
                      fontSize: "11px", 
                      color: "#333", 
                      lineHeight: "1.4",
                      padding: "6px",
                      background: "#f9f9f9",
                      border: "1px solid #eee",
                      borderRadius: "3px"
                    }}
                    dangerouslySetInnerHTML={{ __html: selectedCampground.FacilityDescription }}
                  />
                </div>
              )}

              {selectedCampground.CampsiteAccessible !== undefined && (
                <div style={{ marginBottom: "8px" }}>
                  <strong>Accessible:</strong> {selectedCampground.CampsiteAccessible ? "Yes" : "No"}
                </div>
              )}

              {selectedCampground.ATTRIBUTES && Array.isArray(selectedCampground.ATTRIBUTES) && selectedCampground.ATTRIBUTES.length > 0 && (
                <div style={{ marginBottom: "8px" }}>
                  <strong>Attributes ({selectedCampground.ATTRIBUTES.length}):</strong>
                  <div style={{ marginTop: "4px", paddingLeft: "8px", borderLeft: "2px solid #ccc" }}>
                    {selectedCampground.ATTRIBUTES.slice(0, 15).map((attr, i) => (
                      <div key={i} style={{ marginBottom: "2px", fontSize: "10px" }}>
                        • {attr.AttributeName || attr}: {attr.AttributeValue || ""}
                      </div>
                    ))}
                    {selectedCampground.ATTRIBUTES.length > 15 && (
                      <div style={{ fontSize: "10px", color: "#999" }}>
                        ... and {selectedCampground.ATTRIBUTES.length - 15} more
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div style={{ color: "#999", textAlign: "center", paddingTop: "40px" }}>
            Select a campground to view details
          </div>
        )}
      </div>
    </div>
  );
};

export default CampgroundInfo;
